import i18n from 'i18next';
import { getApiOrigin } from 'src/app/services/baseQuery';
import type { DemoSourceRo } from 'src/app/services/api.generated';

/**
 * Anonymous funnel counter for demo users. Sends the event name, the UI
 * locale and coarse buckets, nothing else: no user or task id, no cookie, no
 * stored id. The backend logs one line (see metrics/editor-metrics.ts) and,
 * when the event carries `source`, adds 1 to that day's per-source counter in
 * demo_daily_counts. `source` is the categorical labels of the visitor's own
 * counted upload (profile `demoSource`, kept in memory only), never a URL.
 */

export type EditorMetricEvent =
  | 'editor_results_shown'
  | 'editor_redaction_applied'
  | 'editor_export_clicked'
  | 'editor_banner_click'
  // Demo contact email form shown (DemoLeadCapture); e and lng only.
  | 'lead_capture_shown'
  // Demo editor tip (DemoEditorGuidance); e, lng, tip and act only.
  | 'editor_tip'
  // A redaction box was added or removed (BoxEditMetrics); e, lng and action only.
  | 'editor_box_edited'
  // Page left after results without edit, anonymise or export; e, lng and bucket only.
  | 'editor_left_no_action';

/** Same closed lists as the backend (src/demo/editor-tips.ts). */
export type EditorTip = 'export_hint' | 'boxes' | 'add_remove' | 'review';
export type EditorTipAct = 'shown' | 'clicked' | 'dismissed';
export type EditorBoxAction = 'add' | 'remove';
export type EditorLeftBucket = 'lt1m' | '1to5m' | 'gt5m';

export type EditorMetricInput = {
  pages?: number;
  suggestions?: number;
  target?: 'pricing' | 'booking';
  /** editor_tip only: which tip and what happened to it. */
  tip?: EditorTip;
  act?: EditorTipAct;
  /** editor_box_edited only. */
  action?: EditorBoxAction;
  /** editor_left_no_action only: dwell time block. */
  bucket?: EditorLeftBucket;
  /** Labels from the profile (auth.demoSource); omitted when null. */
  source?: DemoSourceRo | null;
};

const LOCALES = ['en', 'nl', 'de', 'es', 'it', 'fr', 'pt'];
const ENDPOINT_PATH = '/api/v1/metrics/editor-event';

export const pageBucket = (n: number) =>
  n <= 1 ? '1' : n <= 5 ? '2-5' : n <= 20 ? '6-20' : '21+';

export const suggestionBucket = (n: number) =>
  n <= 0 ? '0' : n <= 10 ? '1-10' : n <= 50 ? '11-50' : '51+';

const trackingDisabled = () =>
  typeof navigator === 'undefined' ||
  navigator.doNotTrack === '1' ||
  (navigator as Navigator & { globalPrivacyControl?: boolean })
    .globalPrivacyControl === true;

const locale = () => {
  const lng = (i18n.resolvedLanguage || i18n.language || 'en').slice(0, 2);
  return LOCALES.includes(lng) ? lng : 'en';
};

/**
 * Events already sent in this task view (editor_tip: per tip/act pair,
 * editor_box_edited: per action); reset when the view is left.
 */
const sent = new Set<string>();

/**
 * In memory only, per task view, for editor_left_no_action: when the results
 * were shown, and whether the visitor edited a box, clicked anonymise or
 * exported. Nothing is stored; only the bucket is ever sent.
 */
let resultsShownAt: number | null = null;
let acted = false;
const ACTION_EVENTS: readonly EditorMetricEvent[] = [
  'editor_box_edited',
  'editor_redaction_applied',
  'editor_export_clicked',
];

export const resetEditorMetricViews = () => {
  sent.clear();
  resultsShownAt = null;
  acted = false;
};

/** lt1m under 60 s, 1to5m from 60 s up to and including 300 s, gt5m above. */
export const dwellBucket = (ms: number): EditorLeftBucket =>
  ms < 60_000 ? 'lt1m' : ms <= 300_000 ? '1to5m' : 'gt5m';

/**
 * On pagehide: report the dwell bucket when results were shown and the
 * visitor did nothing (no box edit, no anonymise click, no export). Once per
 * task view. Same transport and DNT/GPC rule as every other editor event.
 */
export const sendLeftNoAction = (source?: DemoSourceRo | null): void => {
  if (resultsShownAt === null || acted) return;
  sendEditorMetric('editor_left_no_action', {
    bucket: dwellBucket(Date.now() - resultsShownAt),
    source,
  });
};

/**
 * Fire and forget. Call after the UI state is reached and before any heavy
 * request of the same handler. `once` limits it to one send per task view.
 * Not sendBeacon: that attaches cookies on same-origin requests.
 */
export const sendEditorMetric = (
  event: EditorMetricEvent,
  {
    pages,
    suggestions,
    target,
    tip,
    act,
    action,
    bucket,
    source,
  }: EditorMetricInput = {},
  once = true
): void => {
  try {
    // Before the DNT/GPC check: only in-memory flags, nothing leaves the page.
    if (ACTION_EVENTS.includes(event)) acted = true;
    if (event === 'editor_results_shown' && resultsShownAt === null) {
      resultsShownAt = Date.now();
    }
    if (trackingDisabled()) return;
    const isTip = event === 'editor_tip';
    const isBox = event === 'editor_box_edited';
    const isLeft = event === 'editor_left_no_action';
    if (isTip && (!tip || !act)) return;
    if (isBox && !action) return;
    if (isLeft && !bucket) return;
    const key = isTip
      ? `${event}:${tip}:${act}`
      : isBox
      ? `${event}:${action}`
      : event;
    if (once) {
      if (sent.has(key)) return;
      sent.add(key);
    }
    const body: Record<string, unknown> = { e: event, lng: locale() };
    if (isTip) {
      body.tip = tip;
      body.act = act;
    }
    if (isBox) body.action = action;
    if (isLeft) body.bucket = bucket;
    const bare = isTip || isBox || isLeft;
    if (pages !== undefined && !bare) body.pages = pageBucket(pages);
    if (suggestions !== undefined && !bare)
      body.sugg = suggestionBucket(suggestions);
    if (target && !bare) body.target = target;
    if (source && event !== 'lead_capture_shown') {
      body.source = {
        src: source.src,
        med: source.med,
        cmp: source.cmp,
        ref: source.ref,
        gclid: source.gclid === true,
      };
    }
    void fetch(getApiOrigin() + ENDPOINT_PATH, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      keepalive: true,
      credentials: 'omit',
    }).catch(() => undefined);
  } catch {
    // Never affects the editor.
  }
};
